import React, { useState, useEffect } from 'react';
import './AdvancedSearchModal.css';
import searchableFields from '../data/searchableFields';
import artUnits from '../data/artUnits';
import applicationTypes from '../data/applicationTypes';
import statusCodes from '../data/statusCodes';

const AdvancedSearchModal = ({ isOpen, onClose, onApply, initialCriteria = {} }) => {
  const [criteria, setCriteria] = useState({
    // Basic fields
    firstApplicantName: '',
    inventorName: '',
    patentNumber: '',
    docketNumber: '',
    
    // Status filter
    status: '',  // 'granted', 'pending', or ''
    
    // Application details
    applicationTypeCodes: [],  // Changed to array for multi-select
    applicationStatusCodes: [], // Changed to array for multi-select
    groupArtUnitNumbers: [],   // Changed to array for multi-select
    firstInventorToFileIndicator: '',
    
    // Dates
    filingDateFrom: '',
    filingDateTo: '',
    grantDateFrom: '',
    grantDateTo: '',
    
    // Publication
    publicationCategory: '',
    
    // Entity status
    smallEntityStatus: '',
    
    // Boolean operator
    operator: 'AND',
    
    // Filters, Range Filters, and Facets
    filters: [],         // [{field: 'fieldName', value: 'value'}]
    rangeFilters: [],    // [{field: 'fieldName', from: 'start', to: 'end'}]
    facets: []           // ['fieldName1', 'fieldName2']
  });

  const [showArtUnitSelector, setShowArtUnitSelector] = useState(false);
  const [showStatusSelector, setShowStatusSelector] = useState(false);
  const [showAppTypeSelector, setShowAppTypeSelector] = useState(false);

  useEffect(() => {
    if (initialCriteria) {
      setCriteria(prev => ({ ...prev, ...initialCriteria }));
    }
  }, [initialCriteria]);

  const handleChange = (field, value) => {
    setCriteria(prev => ({ ...prev, [field]: value }));
  };

  const buildQuery = () => {
    const parts = [];
    
    // Applicant name with wildcard support
    if (criteria.firstApplicantName) {
      const value = criteria.firstApplicantName.includes('*') 
        ? criteria.firstApplicantName 
        : `${criteria.firstApplicantName}*`;
      parts.push(`applicationMetaData.firstApplicantName:${value}`);
    }
    
    // Inventor name with wildcard support
    if (criteria.inventorName) {
      const value = criteria.inventorName.includes('*') 
        ? criteria.inventorName 
        : `${criteria.inventorName}*`;
      parts.push(`applicationMetaData.inventorBag.inventorNameText:${value}`);
    }
    
    // Patent number
    if (criteria.patentNumber) {
      parts.push(`applicationMetaData.patentNumber:${criteria.patentNumber}`);
    }
    
    // Docket number
    if (criteria.docketNumber) {
      parts.push(`applicationMetaData.docketNumber:${criteria.docketNumber}`);
    }
    
    // Application type codes (multi-select)
    if (criteria.applicationTypeCodes && criteria.applicationTypeCodes.length > 0) {
      const typeQuery = criteria.applicationTypeCodes.map(code => 
        `applicationMetaData.applicationTypeCode:${code}`
      ).join(' OR ');
      parts.push(`(${typeQuery})`);
    }
    
    // Application status codes (multi-select)
    if (criteria.applicationStatusCodes && criteria.applicationStatusCodes.length > 0) {
      const statusQuery = criteria.applicationStatusCodes.map(code => 
        `applicationMetaData.applicationStatusCode:${code}`
      ).join(' OR ');
      parts.push(`(${statusQuery})`);
    }
    
    // Group art units (multi-select)
    if (criteria.groupArtUnitNumbers && criteria.groupArtUnitNumbers.length > 0) {
      const artUnitQuery = criteria.groupArtUnitNumbers.map(unit => 
        `applicationMetaData.groupArtUnitNumber:${unit}`
      ).join(' OR ');
      parts.push(`(${artUnitQuery})`);
    }
    
    // First inventor to file
    if (criteria.firstInventorToFileIndicator) {
      parts.push(`applicationMetaData.firstInventorToFileIndicator:${criteria.firstInventorToFileIndicator}`);
    }
    
    // Filing date range
    if (criteria.filingDateFrom || criteria.filingDateTo) {
      const from = criteria.filingDateFrom || '*';
      const to = criteria.filingDateTo || '*';
      parts.push(`applicationMetaData.filingDate:[${from} TO ${to}]`);
    }
    
    // Grant date range
    if (criteria.grantDateFrom || criteria.grantDateTo) {
      const from = criteria.grantDateFrom || '*';
      const to = criteria.grantDateTo || '*';
      parts.push(`applicationMetaData.grantDate:[${from} TO ${to}]`);
    }
    
    // Publication category
    if (criteria.publicationCategory) {
      parts.push(`applicationMetaData.publicationCategoryBag:${criteria.publicationCategory}`);
    }
    
    // Small entity status
    if (criteria.smallEntityStatus) {
      parts.push(`applicationMetaData.entityStatusData.smallEntityStatusIndicator:${criteria.smallEntityStatus}`);
    }
    
    // Filters (exact value matches)
    if (criteria.filters && criteria.filters.length > 0) {
      criteria.filters.forEach(filter => {
        if (filter.field && filter.value) {
          // Support comma-separated values for OR logic
          const values = filter.value.split(',').map(v => v.trim()).filter(v => v);
          if (values.length > 1) {
            const filterQuery = values.map(v => `${filter.field}:${v}`).join(' OR ');
            parts.push(`(${filterQuery})`);
          } else if (values.length === 1) {
            parts.push(`${filter.field}:${values[0]}`);
          }
        }
      });
    }
    
    // Range Filters (date or number ranges)
    if (criteria.rangeFilters && criteria.rangeFilters.length > 0) {
      criteria.rangeFilters.forEach(rangeFilter => {
        if (rangeFilter.field && (rangeFilter.from || rangeFilter.to)) {
          const from = rangeFilter.from || '*';
          const to = rangeFilter.to || '*';
          parts.push(`${rangeFilter.field}:[${from} TO ${to}]`);
        }
      });
    }
    
    // Join with operator
    return parts.length > 0 ? parts.join(` ${criteria.operator} `) : '*:*';
  };

  const handleApply = () => {
    const query = buildQuery();
    // Pass back complete criteria including query, status, filters, rangeFilters, and facets
    onApply({
      query,
      status: criteria.status || null,
      filters: criteria.filters.filter(f => f.field && f.value),
      rangeFilters: criteria.rangeFilters.filter(rf => rf.field && (rf.from || rf.to)),
      facets: criteria.facets.filter(f => f)
    });
  };

  const handleClear = () => {
    setCriteria({
      firstApplicantName: '',
      inventorName: '',
      patentNumber: '',
      docketNumber: '',
      status: '',
      applicationTypeCodes: [],
      applicationStatusCodes: [],
      groupArtUnitNumbers: [],
      firstInventorToFileIndicator: '',
      filingDateFrom: '',
      filingDateTo: '',
      grantDateFrom: '',
      grantDateTo: '',
      publicationCategory: '',
      smallEntityStatus: '',
      operator: 'AND',
      filters: [],
      rangeFilters: [],
      facets: []
    });
  };

  const toggleArraySelection = (field, value) => {
    setCriteria(prev => {
      const currentArray = prev[field] || [];
      const newArray = currentArray.includes(value)
        ? currentArray.filter(v => v !== value)
        : [...currentArray, value];
      return { ...prev, [field]: newArray };
    });
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content advanced-search-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Advanced Search Criteria</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-body">
          <div className="search-section">
            <h4>Boolean Operator</h4>
            <div className="form-group">
              <label>Combine criteria with:</label>
              <select value={criteria.operator} onChange={(e) => handleChange('operator', e.target.value)}>
                <option value="AND">AND (all must match)</option>
                <option value="OR">OR (any must match)</option>
              </select>
            </div>
          </div>

          <div className="search-section">
            <h4>Basic Information</h4>
            <div className="form-group">
              <label>Applicant/Assignee Name</label>
              <input
                type="text"
                placeholder="e.g., British* (use * for wildcard)"
                value={criteria.firstApplicantName}
                onChange={(e) => handleChange('firstApplicantName', e.target.value)}
              />
              <span className="field-hint">Supports wildcards (*) and regex patterns</span>
            </div>
            
            <div className="form-group">
              <label>Inventor Name</label>
              <input
                type="text"
                placeholder="e.g., Smith* (use * for wildcard)"
                value={criteria.inventorName}
                onChange={(e) => handleChange('inventorName', e.target.value)}
              />
              <span className="field-hint">Supports wildcards (*) and regex patterns</span>
            </div>
            
            <div className="form-group">
              <label>Patent Number</label>
              <input
                type="text"
                placeholder="e.g., 10123456"
                value={criteria.patentNumber}
                onChange={(e) => handleChange('patentNumber', e.target.value)}
              />
            </div>
            
            <div className="form-group">
              <label>Docket Number</label>
              <input
                type="text"
                placeholder="e.g., ABC-123"
                value={criteria.docketNumber}
                onChange={(e) => handleChange('docketNumber', e.target.value)}
              />
            </div>
          </div>

          <div className="search-section">
            <h4>Application Details</h4>
            
            {/* Status Filter */}
            <div className="form-group">
              <label>Status Filter</label>
              <select value={criteria.status} onChange={(e) => handleChange('status', e.target.value)}>
                <option value="">All filings</option>
                <option value="granted">Granted (has grant date)</option>
                <option value="pending">Pending (no grant date)</option>
              </select>
              <span className="field-hint">Filters by presence/absence of grant date</span>
            </div>
            
            {/* Application Type Codes */}
            <div className="form-group">
              <label>Application Type Codes ({criteria.applicationTypeCodes.length} selected)</label>
              <button 
                type="button"
                className="btn btn-selector"
                onClick={() => setShowAppTypeSelector(!showAppTypeSelector)}
              >
                {showAppTypeSelector ? 'Hide' : 'Show'} Application Types
              </button>
              {showAppTypeSelector && (
                <div className="multi-select-box">
                  {applicationTypes.map(type => (
                    <label key={type.code} className="multi-select-item">
                      <input
                        type="checkbox"
                        checked={criteria.applicationTypeCodes.includes(type.code)}
                        onChange={() => toggleArraySelection('applicationTypeCodes', type.code)}
                      />
                      <div className="multi-select-label">
                        <strong>{type.code}</strong> - {type.label}
                        <span className="multi-select-description">{type.description}</span>
                      </div>
                    </label>
                  ))}
                </div>
              )}
              {criteria.applicationTypeCodes.length > 0 && (
                <div className="selected-items">
                  {criteria.applicationTypeCodes.map(code => (
                    <span key={code} className="selected-chip">
                      {code}
                      <button onClick={() => toggleArraySelection('applicationTypeCodes', code)}>×</button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            
            {/* Application Status Codes */}
            <div className="form-group">
              <label>Application Status Codes ({criteria.applicationStatusCodes.length} selected)</label>
              <button 
                type="button"
                className="btn btn-selector"
                onClick={() => setShowStatusSelector(!showStatusSelector)}
              >
                {showStatusSelector ? 'Hide' : 'Show'} Status Codes
              </button>
              {showStatusSelector && (
                <div className="multi-select-box">
                  {statusCodes.map(status => (
                    <label key={status.code} className="multi-select-item">
                      <input
                        type="checkbox"
                        checked={criteria.applicationStatusCodes.includes(status.code)}
                        onChange={() => toggleArraySelection('applicationStatusCodes', status.code)}
                      />
                      <div className="multi-select-label">
                        <strong>{status.code}</strong>
                        <span className="multi-select-description">{status.description}</span>
                      </div>
                    </label>
                  ))}
                </div>
              )}
              {criteria.applicationStatusCodes.length > 0 && (
                <div className="selected-items">
                  {criteria.applicationStatusCodes.map(code => (
                    <span key={code} className="selected-chip">
                      {code}
                      <button onClick={() => toggleArraySelection('applicationStatusCodes', code)}>×</button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            
            <div className="form-group">
              <label>First Inventor to File</label>
              <select value={criteria.firstInventorToFileIndicator} onChange={(e) => handleChange('firstInventorToFileIndicator', e.target.value)}>
                <option value="">Any</option>
                <option value="Y">Yes</option>
                <option value="N">No</option>
              </select>
            </div>
          </div>

          <div className="search-section">
            <h4>Dates</h4>
            <div className="form-group">
              <label>Filing Date Range</label>
              <div className="date-range-inputs">
                <input
                  type="date"
                  placeholder="From"
                  value={criteria.filingDateFrom}
                  onChange={(e) => handleChange('filingDateFrom', e.target.value)}
                />
                <span>TO</span>
                <input
                  type="date"
                  placeholder="To"
                  value={criteria.filingDateTo}
                  onChange={(e) => handleChange('filingDateTo', e.target.value)}
                />
              </div>
              <span className="field-hint">Format: YYYY-MM-DD</span>
            </div>
            
            <div className="form-group">
              <label>Grant Date Range</label>
              <div className="date-range-inputs">
                <input
                  type="date"
                  placeholder="From"
                  value={criteria.grantDateFrom}
                  onChange={(e) => handleChange('grantDateFrom', e.target.value)}
                />
                <span>TO</span>
                <input
                  type="date"
                  placeholder="To"
                  value={criteria.grantDateTo}
                  onChange={(e) => handleChange('grantDateTo', e.target.value)}
                />
              </div>
              <span className="field-hint">Format: YYYY-MM-DD</span>
            </div>
          </div>

          <div className="search-section">
            <h4>Classification & Status</h4>
            
            {/* Group Art Unit Numbers */}
            <div className="form-group">
              <label>Group Art Unit Numbers ({criteria.groupArtUnitNumbers.length} selected)</label>
              <button 
                type="button"
                className="btn btn-selector"
                onClick={() => setShowArtUnitSelector(!showArtUnitSelector)}
              >
                {showArtUnitSelector ? 'Hide' : 'Show'} Art Units
              </button>
              {showArtUnitSelector && (
                <div className="multi-select-box">
                  {artUnits.map(unit => (
                    <label key={unit.code} className="multi-select-item">
                      <input
                        type="checkbox"
                        checked={criteria.groupArtUnitNumbers.includes(unit.code)}
                        onChange={() => toggleArraySelection('groupArtUnitNumbers', unit.code)}
                      />
                      <div className="multi-select-label">
                        <strong>{unit.code}</strong>
                        <span className="multi-select-description">{unit.description}</span>
                      </div>
                    </label>
                  ))}
                </div>
              )}
              {criteria.groupArtUnitNumbers.length > 0 && (
                <div className="selected-items">
                  {criteria.groupArtUnitNumbers.map(code => (
                    <span key={code} className="selected-chip">
                      {code}
                      <button onClick={() => toggleArraySelection('groupArtUnitNumbers', code)}>×</button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            
            <div className="form-group">
              <label>Publication Category</label>
              <input
                type="text"
                placeholder="e.g., A1, B1"
                value={criteria.publicationCategory}
                onChange={(e) => handleChange('publicationCategory', e.target.value)}
              />
            </div>
            
            <div className="form-group">
              <label>Small Entity Status</label>
              <select value={criteria.smallEntityStatus} onChange={(e) => handleChange('smallEntityStatus', e.target.value)}>
                <option value="">Any</option>
                <option value="true">Small Entity</option>
                <option value="false">Large Entity</option>
              </select>
            </div>
          </div>

          <div className="search-section">
            <h4>Filters</h4>
            <p className="section-description">Apply exact value filters to any field in the result data. You can add multiple values separated by commas.</p>
            {criteria.filters.map((filter, idx) => (
              <div key={idx} className="filter-row">
                <select
                  value={filter.field}
                  onChange={(e) => {
                    const newFilters = [...criteria.filters];
                    newFilters[idx].field = e.target.value;
                    handleChange('filters', newFilters);
                  }}
                  className="filter-field-select"
                >
                  <option value="">-- Select Field --</option>
                  {searchableFields.map(sf => (
                    <option key={sf.field} value={sf.field}>{sf.label}</option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder="Value (e.g., UTL,DES for multiple)"
                  value={filter.value}
                  onChange={(e) => {
                    const newFilters = [...criteria.filters];
                    newFilters[idx].value = e.target.value;
                    handleChange('filters', newFilters);
                  }}
                  className="filter-value-input"
                />
                <button 
                  className="btn-remove" 
                  onClick={() => handleChange('filters', criteria.filters.filter((_, i) => i !== idx))}
                  title="Remove filter"
                >
                  ×
                </button>
              </div>
            ))}
            <button 
              className="btn btn-add" 
              onClick={() => handleChange('filters', [...criteria.filters, { field: '', value: '' }])}
            >
              + Add Filter
            </button>
            <span className="field-hint">Examples: applicationMetaData.applicationTypeCode UTL,DES | applicationMetaData.filingDate 2023-10-18</span>
          </div>

          <div className="search-section">
            <h4>Range Filters</h4>
            <p className="section-description">Filter by date or number ranges. Applicable for numeric and date fields.</p>
            {criteria.rangeFilters.map((rangeFilter, idx) => (
              <div key={idx} className="filter-row range-filter-row">
                <select
                  value={rangeFilter.field}
                  onChange={(e) => {
                    const newRangeFilters = [...criteria.rangeFilters];
                    newRangeFilters[idx].field = e.target.value;
                    handleChange('rangeFilters', newRangeFilters);
                  }}
                  className="filter-field-select"
                >
                  <option value="">-- Select Field --</option>
                  {searchableFields.filter(sf => sf.type === 'date' || sf.type === 'number').map(sf => (
                    <option key={sf.field} value={sf.field}>{sf.label}</option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder="From (e.g., 2010-01-01)"
                  value={rangeFilter.from}
                  onChange={(e) => {
                    const newRangeFilters = [...criteria.rangeFilters];
                    newRangeFilters[idx].from = e.target.value;
                    handleChange('rangeFilters', newRangeFilters);
                  }}
                  className="range-input"
                />
                <span className="range-separator">:</span>
                <input
                  type="text"
                  placeholder="To (e.g., 2020-01-01)"
                  value={rangeFilter.to}
                  onChange={(e) => {
                    const newRangeFilters = [...criteria.rangeFilters];
                    newRangeFilters[idx].to = e.target.value;
                    handleChange('rangeFilters', newRangeFilters);
                  }}
                  className="range-input"
                />
                <button 
                  className="btn-remove" 
                  onClick={() => handleChange('rangeFilters', criteria.rangeFilters.filter((_, i) => i !== idx))}
                  title="Remove range filter"
                >
                  ×
                </button>
              </div>
            ))}
            <button 
              className="btn btn-add" 
              onClick={() => handleChange('rangeFilters', [...criteria.rangeFilters, { field: '', from: '', to: '' }])}
            >
              + Add Range Filter
            </button>
            <span className="field-hint">Examples: applicationMetaData.grantDate 2010-01-01:2020-01-01 | applicationMetaData.applicationStatusCode 150:160</span>
          </div>

          <div className="search-section">
            <h4>Facets</h4>
            <p className="section-description">Select fields to group and count results by. Returns aggregated counts for each unique value.</p>
            <div className="facets-container">
              {criteria.facets.map((facet, idx) => (
                <div key={idx} className="facet-row">
                  <select
                    value={facet}
                    onChange={(e) => {
                      const newFacets = [...criteria.facets];
                      newFacets[idx] = e.target.value;
                      handleChange('facets', newFacets);
                    }}
                    className="facet-select"
                  >
                    <option value="">-- Select Field --</option>
                    {searchableFields.map(sf => (
                      <option key={sf.field} value={sf.field}>{sf.label}</option>
                    ))}
                  </select>
                  <button 
                    className="btn-remove" 
                    onClick={() => handleChange('facets', criteria.facets.filter((_, i) => i !== idx))}
                    title="Remove facet"
                  >
                    ×
                  </button>
                </div>
              ))}
              <button 
                className="btn btn-add" 
                onClick={() => handleChange('facets', [...criteria.facets, ''])}
              >
                + Add Facet Field
              </button>
            </div>
            <span className="field-hint">Common facet fields: applicationMetaData.applicationTypeCode, applicationMetaData.applicationStatusCode, applicationMetaData.groupArtUnitNumber</span>
          </div>
        </div>
        
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={handleClear}>Clear All</button>
          <div className="modal-footer-actions">
            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={handleApply}>Apply Search</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvancedSearchModal;
